import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Column from '@amzn/meridian/column';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import type { TagType } from '@amzn/meridian/tag/tag';
import Icon from '@amzn/meridian/icon';
import type { IconColor } from '@amzn/meridian/icon/icon';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { useWorkflowExecutionPlanControllerFindManyQuery } from 'src/store/leela-api';
import { type WorkflowExecutionPlanDto, type WorkflowStageDto } from 'src/store/generated/leela-api-generated';
import { Loader } from 'src/components/common/Loader';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { NoResultsTableRow } from 'src/components/common/data-table-action-bar/NoResultsTableRow';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { colorSuccess600, colorWarning500, colorError600, colorGray400, colorGray200 } from '@amzn/meridian-tokens/base/color';
import chevronDownLargeTokens from '@amzn/meridian-tokens/base/icon/chevron-down-large';
import chevronRightLargeTokens from '@amzn/meridian-tokens/base/icon/chevron-right-large';
import checkCircleFilledTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import clockTokens from '@amzn/meridian-tokens/base/icon/clock';
import closeCircleTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import minusCircleTokens from '@amzn/meridian-tokens/base/icon/minus-circle';

interface ExecutionWorkflowsProps {
  campaignId: string;
}

interface PaginatedResponse {
  data: WorkflowExecutionPlanDto[];
  meta: { pagination: { page: number; pageSize: number; pageCount: number; total: number } };
}

type WorkflowStatus = WorkflowExecutionPlanDto['workflowStatus'];
type StageStatus = WorkflowStageDto['status'];

const WorkflowStatusValues: Record<string, WorkflowStatus> = {
  QUEUED: 'QUEUED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETE: 'COMPLETE',
  FAILED: 'FAILED',
} as const;

const StageStatusValues: Record<string, StageStatus> = {
  QUEUED: 'QUEUED',
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  COMPLETE: 'COMPLETE',
  FAILED: 'FAILED',
} as const;

const getStatusTagType = (status: WorkflowStatus): TagType => {
  switch (status) {
    case WorkflowStatusValues.QUEUED:
    case WorkflowStatusValues.IN_PROGRESS:
      return 'warning';
    case WorkflowStatusValues.COMPLETE:
      return 'success';
    default:
      return 'error';
  }
};

const getStageStatusColor = (status: StageStatus): string => {
  switch (status) {
    case StageStatusValues.COMPLETE:
      return colorSuccess600;
    case StageStatusValues.RUNNING:
      return colorWarning500;
    case StageStatusValues.FAILED:
      return colorError600;
    default:
      return colorGray400;
  }
};

interface StageIconInfo {
  tokens: object;
  color: IconColor;
}

const getStageStatusIcon = (status: StageStatus): StageIconInfo => {
  switch (status) {
    case StageStatusValues.COMPLETE:
      return { tokens: checkCircleFilledTokens, color: 'success' };
    case StageStatusValues.RUNNING:
      return { tokens: clockTokens, color: 'warning' };
    case StageStatusValues.FAILED:
      return { tokens: closeCircleTokens, color: 'error' };
    default:
      return { tokens: minusCircleTokens, color: 'secondary' };
  }
};

const formatDuration = (start?: string | null, end?: string | null): string => {
  if (!start) return '';
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  const diffMs = endMs - startMs;
  if (diffMs < 1000) return `${diffMs}ms`;
  if (diffMs < 60000) return `${(diffMs / 1000).toFixed(1)}s`;
  return `${(diffMs / 60000).toFixed(1)}m`;
};

const formatOperationType = (type: string): string => {
  return type.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
};

const formatStageName = (stage: string): string => {
  return stage.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

const formatResult = (result?: object | null): string | null => {
  if (!result) return null;
  return Object.entries(result)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};

const StageProgress: React.FC<{ stages: WorkflowStageDto[] }> = ({ stages }) => {
  const completed = stages.filter(s => s.status === StageStatusValues.COMPLETE).length;
  const failed = stages.filter(s => s.status === StageStatusValues.FAILED).length;
  const total = stages.length;

  if (failed > 0) return <Tag type="error">{`${completed}/${total} (${failed} failed)`}</Tag>;
  if (completed === total) return <Tag type="success">{`${completed}/${total}`}</Tag>;
  return <Tag type="warning">{`${completed}/${total}`}</Tag>;
};

const getStageTagType = (status: StageStatus): TagType => {
  switch (status) {
    case StageStatusValues.COMPLETE:
      return 'success';
    case StageStatusValues.FAILED:
      return 'error';
    case StageStatusValues.RUNNING:
      return 'warning';
    default:
      return 'neutral';
  }
};

const StageTimeline: React.FC<{ stages: WorkflowStageDto[] }> = ({ stages }) => {
  const sorted = [...stages].sort((a, b) => a.order - b.order);

  return (
    <Column spacing="200" spacingInset="400">
      {sorted.map((stage, idx) => {
        const color = getStageStatusColor(stage.status);
        const iconInfo = getStageStatusIcon(stage.status);
        const duration = formatDuration(stage.startedAt, stage.completedAt);
        const resultText = formatResult(stage.result);
        const isLast = idx === sorted.length - 1;
        const isInactive = stage.status === StageStatusValues.QUEUED || stage.status === StageStatusValues.PENDING;

        return (
          <Row alignmentVertical="top" key={stage.stage} spacing="300">
            <Column alignmentHorizontal="center" spacing="none" width={16}>
              <span style={{ display: 'flex', transform: 'scale(0.7)' }}>
                <Icon color={iconInfo.color} tokens={iconInfo.tokens} />
              </span>
              {!isLast && <div style={{ width: '2px', height: '24px', backgroundColor: isInactive ? colorGray200 : color, margin: '2px auto' }} />}
            </Column>
            <Column spacing="none">
              <Row alignmentVertical="center" spacing="200">
                <span style={{ display: 'inline-block', minWidth: '140px' }}>
                  <Text color={isInactive ? 'secondary' : 'primary'} type="b200">
                    {formatStageName(stage.stage)}
                  </Text>
                </span>
                <Tag type={getStageTagType(stage.status)}>
                  {stage.status}
                </Tag>
                {duration && (
                  <Text color="secondary" type="b100">{duration}</Text>
                )}
              </Row>
              {resultText && (
                <Text color="secondary" type="b100">{resultText}</Text>
              )}
              {stage.status === StageStatusValues.FAILED && stage.retryCount !== undefined && (
                <Text color="error" type="b100">
                  {`Retry ${stage.retryCount}/${stage.maxRetries ?? 3}`}
                  {stage.retryAfter && ` — next: ${moment(stage.retryAfter).fromNow()}`}
                </Text>
              )}
            </Column>
          </Row>
        );
      })}
    </Column>
  );
};

export const ExecutionWorkflows: React.FC<ExecutionWorkflowsProps> = ({ campaignId }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [autoExpanded, setAutoExpanded] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20 });
  const errorShownRef = useRef(false);

  const itemsPerPageOptions = [20, 50, 100];

  const { data: rawData, error, isLoading } = useWorkflowExecutionPlanControllerFindManyQuery({
    campaignId,
    pageSize: pagination.pageSize,
    page: pagination.page,
  }, { pollingInterval: 10000 });

  const data = rawData as PaginatedResponse | undefined;
  const workflows = data?.data ?? [];
  const pageCount = data?.meta?.pagination?.pageCount ?? 1;

  if (!autoExpanded && workflows.length > 0) {
    setExpandedId(workflows[0].workflowId);
    setAutoExpanded(true);
  }

  useEffect(() => {
    if (error && !errorShownRef.current) {
      errorShownRef.current = true;
      createToast({
        type: AlertType.ERROR,
        message: t('form_workflows_execution_error_fetch'),
      });
    } else if (!error) {
      errorShownRef.current = false;
    }
  }, [error, createToast, t]);

  const toggleExpand = (workflowId: string) => {
    setExpandedId(prev => prev === workflowId ? null : workflowId);
  };

  return (
    <Loader isLoading={isLoading}>
      <Column>
        <Table
          data-testid="execution-workflows-table"
          headerRows={1}
          rowComponents={[TableRow, NoResultsTableRow]}
          showDividers={true}
        >
          <TableRow key="execution-workflows-header">
            <TableCell></TableCell>
            <TableCell>{t('form_global_id')}</TableCell>
            <TableCell>{t('form_global_status')}</TableCell>
            <TableCell>{t('form_workflows_operation')}</TableCell>
            <TableCell>{t('form_global_created_at')}</TableCell>
            <TableCell>{t('form_workflows_stages')}</TableCell>
          </TableRow>

          {workflows?.length === 0 && <NoResultsTableRow data-testid="no-results-row" span={6} />}
          {workflows.flatMap(workflow => [
            <TableRow
              data-testid={`execution-workflow-row-${workflow.workflowId}`}
              highlightOnHover
              key={workflow.workflowId}
              onClick={() => { toggleExpand(workflow.workflowId); }}
            >
              <TableCell>
                <Icon tokens={expandedId === workflow.workflowId ? chevronDownLargeTokens : chevronRightLargeTokens} />
              </TableCell>
              <TableCell>
                <Text type="b200">{workflow.workflowId}</Text>
              </TableCell>
              <TableCell>
                <Tag type={getStatusTagType(workflow.workflowStatus)}>{workflow.workflowStatus}</Tag>
              </TableCell>
              <TableCell>{formatOperationType(workflow.operationType)}</TableCell>
              <TableCell>
                <div title={workflow.createdAt}>
                  {moment(workflow.createdAt).fromNow()}
                </div>
              </TableCell>
              <TableCell>
                <StageProgress stages={workflow.stages} />
              </TableCell>
            </TableRow>,
            ...(expandedId === workflow.workflowId
              ? [
              <TableRow data-testid={`execution-workflow-detail-${workflow.workflowId}`} key={`${workflow.workflowId}-detail`}>
                <TableCell columnSpan={6}>
                  <StageTimeline stages={workflow.stages} />
                </TableCell>
              </TableRow>,
                ]
              : []),
          ])}
        </Table>

        <TablePagination
          currentPage={pagination.page}
          itemsPerPage={pagination.pageSize}
          itemsPerPageOptions={itemsPerPageOptions}
          pageCount={pageCount}
          onItemsPerPageChange={(pageSize: number) => {
            setPagination(prev => ({
              ...prev,
              page: 1,
              pageSize,
            }));
          }}
          onPageChange={(newPage: number) => {
            setPagination(prev => ({
              ...prev,
              page: newPage,
            }));
          }}
        />
      </Column>
    </Loader>
  );
};
